import React, { Suspense } from 'react';
import type { AtlassianTileType, RenderFn } from '@atlassian/forge-ui-types';
import type { ObjectTileSize } from '@atlaskit/object';
import warnOnce from '../../../utils/warn-once';
import { AtlassianTileComponents } from './__generated__';

export type AtlassianTileProps = {
	/**
	 * The glyph type of AtlassianTile to render. This will automatically set the color.
	 * Available options are derived from @atlaskit/object metadata and will
	 * automatically include any new object types added to the design system.
	 */
	glyph: AtlassianTileType;
	/**
	 * Whether the AtlassianTile should be bold in appearance.
	 * Defaults to `false`.
	 */
	isBold?: boolean;
	/**
	 * The label for the AtlassianTile. If decorative, set to empty string.
	 * Defaults to a human-readable version of the icon type.
	 */
	label?: string;
	/**
	 * The size of the tile.
	 * - `xsmall`: 20px
	 * - `small`: 24px
	 * - `medium`: 32px
	 * - `large`: 40px
	 * - `xlarge`: 48px
	 * Defaults to `medium`.
	 */
	size?: ObjectTileSize;
	/**
	 * Test ID for testing
	 */
	testId?: string;
};

/**
 * An AtlassianTile is an icon that represents an Atlassian-specific content type, displayed in a tile.
 * The glyph prop automatically sets the color for the object.
 */
export const AtlassianTile = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { glyph, label, size, isBold, testId } = props.forgeDoc.props as AtlassianTileProps;

	const Component = AtlassianTileComponents[glyph];

	if (!Component) {
		warnOnce(`[@forge/react: AtlassianTile] no object tile found for glyph "${glyph}".`);
		return <></>;
	}

	return (
		<Suspense fallback={null}>
			<Component testId={testId} label={label} size={size} isBold={isBold} />
		</Suspense>
	);
};
